import React, { useState, useEffect, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";

interface RouteFilters {
  utilityService: string;
  readType: string;
}

interface RouteFiltersComponentProps {
  utilityService: string;
  readType: string;
  utilities: any[];
  meterTypes: any[];
  onApplyFilters: (filters: RouteFilters) => void;
  onResetFilters: () => void;
}

export const RouteFiltersComponent: React.FC<RouteFiltersComponentProps> = ({
  utilityService,
  readType,
  utilities,
  meterTypes,
  onApplyFilters,
  onResetFilters,
}) => {
  // Transform utilities and meterTypes to options format
  const utilityOptions = useMemo(
    () =>
      utilities
        ?.filter((utility) => utility.isActive === true)
        .map((utility) => ({
          value: utility.name,
          label: utility.name,
        })) || [],
    [utilities]
  );

  const meterTypeOptions = useMemo(
    () =>
      meterTypes?.map((type) => ({
        value: type.key,
        label: type.value,
      })) || [],
    [meterTypes]
  );

  // Define filter fields
  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "utilityService",
        label: "Utility Service",
        type: "select",
        placeholder: "All Utilities",
        options: utilityOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "readType",
        label: "Read Type",
        type: "select",
        placeholder: "All Types",
        options: meterTypeOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
    ],
    [utilityOptions, meterTypeOptions]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Sync form values when URL params change
  useEffect(() => {
    form.reset({
      utilityService: decodeURIComponent(utilityService) || "",
      readType: decodeURIComponent(readType) || "",
    });
  }, [utilityService, readType, form]);

  const handleFilterSubmit = () => {
    const formValues = form.getValues();
    onApplyFilters({
      utilityService: formValues.utilityService || "",
      readType: formValues.readType || "",
    });
  };

  const handleFilterReset = () => {
    form.reset({
      utilityService: "",
      readType: "",
    });
    onResetFilters();
  };

  const handleFieldChange = (field: string, value: string) => {
    // Add any field-specific logic here if needed
    // For example, if selecting a utility should trigger additional actions
  };

  return (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        onFieldChange={handleFieldChange}
        gridColumns={2}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            buttonContainer: "flex justify-end gap-4",
          },
        }}
      />
    </div>
  );
};
